import { NavLink } from "@/components/links";
import { StatePage } from "@/components/state-page";
import { orpc } from "@/utils/clients/orpc";
import { useHasPermission } from "@/utils/permissions";
import { Card } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import { t } from "i18next";
import { Activity } from "react";
import AgencyForm from "./-edit";
import GeneralSettings from "./-general";
import NationalIdSettings from "./-national-id";
import { SettingsTabsSchema, TABS } from "./-utils";

export const Route = createFileRoute("/_authenticated/settings/")({
  component: RouteComponent,
  validateSearch: SettingsTabsSchema,
  loaderDeps: ({ search }) => [search.tab] as const,
  loader: ({ context, deps: [tab] }) => {
    if (tab === "agency" && context.auth.user.agencyId) {
      void context.queryClient.prefetchQuery(
        orpc.agency.get.queryOptions({
          input: { id: context.auth.user.agencyId },
        }),
      );
    }
  },
  errorComponent: ({ error, reset }) => (
    <StatePage
      error={error}
      reset={reset}
      resourceType={undefined}
      emptyResourceType={undefined}
    />
  ),
  notFoundComponent: () => (
    <StatePage
      isNotFound
      resourceType={undefined}
      emptyResourceType={undefined}
    />
  ),
});

function RouteComponent() {
  const { tab = "general" } = Route.useSearch();
  const hasAdminPermission = useHasPermission(true);
  const hasNationalIdPermission = useHasPermission(["nationalId:get"]);

  return (
    <div className="flex grow flex-col gap-md md:gap-lg">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 py-md md:justify-start">
        <p className="text-2xl font-semibold md:text-3xl">
          {t("settings.title")}
        </p>
      </div>

      <div className="flex flex-col gap-md lg:flex-row lg:gap-lg">
        <div className="flex w-full flex-col gap-md lg:w-64 lg:shrink-0">
          <Tabs activeTab={tab} />
        </div>

        <div className="flex grow flex-col">
          <Activity mode={tab === "general" ? "visible" : "hidden"}>
            <GeneralSettings />
          </Activity>
          <Activity
            mode={tab === "agency" && hasAdminPermission ? "visible" : "hidden"}
          >
            <AgencyForm />
          </Activity>
          <Activity
            mode={
              tab === "national-id" && hasNationalIdPermission
                ? "visible"
                : "hidden"
            }
          >
            <NationalIdSettings />
          </Activity>
        </div>
      </div>
    </div>
  );
}

function Tabs({ activeTab }: { activeTab: (typeof TABS)[number]["id"] }) {
  return (
    <Card withBorder className="overflow-hidden p-0">
      <div className="flex">
        <div className="flex w-full flex-col">
          {TABS.map((tab) => (
            <TabItem key={tab.id} tab={tab} activeTab={activeTab} />
          ))}
        </div>
      </div>
    </Card>
  );
}

function TabItem({
  tab,
  activeTab,
}: {
  tab: (typeof TABS)[number];
  activeTab: (typeof TABS)[number]["id"];
}) {
  const hasPermission = useHasPermission(tab.permission);
  if (!hasPermission) return null;
  return (
    <NavLink
      key={tab.id}
      to="."
      replace
      resetScroll={false}
      search={() => ({ tab: tab.id })}
      leftSection={<tab.icon className="size-4" />}
      active={activeTab === tab.id}
      className="not-data-[active=true]:dark:hover:bg-(--mantine-color-dark-5)"
      label={tab.label}
    />
  );
}
